import { createSignal, For, Show } from 'solid-js';
import { RadioFieldAttributes } from '../mautic-form-types';
import { formatLabel } from '../utils';

export const RadioGroup = ({ fieldData }: { fieldData: RadioFieldAttributes }) => {
    const { alias, defaultValue, errors, id, isRequired, label, properties, radioInputClasses, handleChange, variant, errorInputClasses, errorId } = fieldData;
    const error = () => Boolean(errors[alias]);
    const options = properties?.optionlist?.list ?? null;
    const initialValue = options && options.length === 1 ? defaultValue ?? false : defaultValue ?? '';
    const [chosen, setChosen] = createSignal(initialValue);

    const stringID = String(id);

    const handleRadioChange = (value: string) => {
        const newValue = options && options.length === 1 ? !chosen() : value;
        setChosen(newValue);
        handleChange(alias, newValue);
    };

    const checkValue = (value: string) => {
        const chosenSignal = chosen();
        // Single option: boolean toggle
        if (typeof chosenSignal === 'boolean') {
            return chosenSignal;
        }
        // Multiple options: string comparison
        return value === chosenSignal;
    };

    return (
        <Show when={options && options.length > 0}>
            <fieldset {...(error() && { 'aria-describedby': errorId })}>
                <legend
                    class="mb-xxs"
                    classList={{
                        'text-secondary-200': variant === 'dark',
                        'text-neutral-900': variant === 'outline-dark',
                        'text-white': variant === 'light' || variant === 'outline-light',
                    }}
                >
                    {formatLabel(label, isRequired)}
                </legend>
                <div class="gap-xxs flex flex-col">
                    <For each={options}>
                        {(option) => (
                            <label class="relative flex cursor-pointer">
                                <input
                                    {...(error() && { 'aria-invalid': true })}
                                    class={radioInputClasses}
                                    classList={{
                                        border: !checkValue(option.value),
                                        'bg-secondary-200 border border-blackTransparent-40':
                                            checkValue(option.value) && (variant === 'dark' || variant === 'outline-light'),
                                        '!bg-neutral-900 border !border-secondary-200':
                                            checkValue(option.value) && (variant === 'light' || variant === 'outline-dark'),
                                        [errorInputClasses || '']: error(),
                                    }}
                                    type="radio"
                                    name={alias}
                                    id={stringID + option.value}
                                    value={option.value}
                                    required={isRequired}
                                    checked={checkValue(option.value)}
                                    onChange={(e) => handleRadioChange(e.target.value)}
                                />
                                <span class="pl-8">{option.label}</span>
                            </label>
                        )}
                    </For>
                </div>
            </fieldset>
        </Show>
    );
};
